import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import WatchLaterOutlinedIcon from "@mui/icons-material/WatchLaterOutlined";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";
import { CaseServices } from "../../../services/CaseServices";
import { handleApiErrorResponses } from "../../../utils/ApisErrorHandling";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "80%",
  height: "65%",
  bgcolor: "background.paper",
  borderRadius: "24px",
  boxShadow: 24,
  outline: "none",
  padding: "28px",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

const hasRejectionReason = (history) => {
  return (
    Array.isArray(history?.rejectionReason) &&
    history.rejectionReason.some((item) => item?.rejectionReason?.trim())
  );
};

const hasDocumentRejectionReason = (document) => {
  return Boolean(
    document?.rejectionReason?.trim() || hasRejectionReason(document?.history),
  );
};

const formatRejectionDate = (date) => {
  if (!date) return "";

  const formattedDate = new Date(date);

  const datePart = formattedDate.toLocaleDateString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

  const timePart = formattedDate.toLocaleTimeString("en-IN", {
    timeZone: "Asia/Kolkata",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  return `${datePart} ${timePart}`;
};

const RejectionReasonHistoryModal = ({ documentDetails, open, setOpen }) => {
  const [historyType, setHistoryType] = useState("complete");
  const [currentPage, setCurrentPage] = useState(0);

  const [deletedDocsData, setDeletedDocsData] = useState([]);
  const [loadingDeletedDocs, setLoadingDeletedDocs] = useState(false);

  useEffect(() => {
    if (open) {
      setHistoryType("complete");
      setCurrentPage(0);
      setDeletedDocsData([]);
      getDeletedDocs(false);
    }
  }, [open]);

  const getDeletedDocs = async (switchToDeleted = true) => {
    if (!documentDetails?.id) return;

    setLoadingDeletedDocs(true);

    try {
      const res = await CaseServices?.getDeletedDocUrls(documentDetails.id);

      const deletedDocuments = res?.data?.doc?.documenturlmasters || [];

      setDeletedDocsData(
        Array.isArray(deletedDocuments) ? deletedDocuments : [],
      );

      setCurrentPage(0);

      if (switchToDeleted) {
        setHistoryType("deleted");
      }
    } catch (error) {
      handleApiErrorResponses(error);
      setDeletedDocsData([]);
    } finally {
      setLoadingDeletedDocs(false);
    }
  };

  const wholeDocumentHistoryReasons =
    documentDetails?.history?.rejectionReason?.filter((item) =>
      item?.rejectionReason?.trim(),
    ) || [];

  const wholeDocumentReasons = [
    ...wholeDocumentHistoryReasons,
    ...(documentDetails?.rejectionReason?.trim() &&
    !wholeDocumentHistoryReasons.some(
      (item) =>
        item?.rejectionReason?.trim() ===
        documentDetails.rejectionReason.trim(),
    )
      ? [
          {
            rejectionReason: documentDetails.rejectionReason,
            rejectionDate: documentDetails.updatedAt,
          },
        ]
      : []),
  ];

  const currentRejectedDocuments =
    documentDetails?.documenturlmasters?.filter((document) => {
      const isCurrentDocument = document?.deleted !== true;

      const hasReason = hasDocumentRejectionReason(document);

      return isCurrentDocument && hasReason;
    }) || [];

  const deletedRejectedDocuments =
    deletedDocsData?.filter((document) => {
      const hasReason = hasDocumentRejectionReason(document);

      return hasReason;
    }) || [];

  const activeRejectedDocuments =
    historyType === "deleted"
      ? deletedRejectedDocuments
      : currentRejectedDocuments;

  const currentDocument = activeRejectedDocuments[currentPage];

  const currentDocumentRejectionReasons =
    currentDocument?.history?.rejectionReason?.filter((item) =>
      item?.rejectionReason?.trim(),
    ) ||
    (currentDocument?.rejectionReason?.trim()
      ? [
          {
            rejectionReason: currentDocument.rejectionReason,
            rejectionDate: currentDocument.updatedAt,
          },
        ]
      : []);

  // const currentDocument = activeRejectedDocuments[currentPage];

  // const currentDocument = rejectedDocuments[currentPage];

  const closeModal = () => {
    setHistoryType("complete");
    setCurrentPage(0);
    setDeletedDocsData([]);
    setOpen(false);
  };

  const showCurrentDocuments = () => {
    if (historyType === "current") {
      showCompleteDocumentReasons();
      return;
    }

    setHistoryType("current");
    setCurrentPage(0);
  };

  const showDeletedDocuments = () => {
    if (historyType === "deleted") {
      showCompleteDocumentReasons();
      return;
    }

    getDeletedDocs(true);
  };

  const showCompleteDocumentReasons = () => {
    setHistoryType("complete");
    setCurrentPage(0);
  };

  const handlePrevious = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 0));
  };

  const handleNext = () => {
    setCurrentPage((prev) =>
      Math.min(prev + 1, activeRejectedDocuments.length - 1),
    );
  };

  return (
    <Modal
      open={open}
      onClose={closeModal}
      aria-labelledby="rejection-reasons-title"
    >
      <Box sx={modalStyle}>
        {/* Header */}
        <div
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderBottom: "1px solid #E4E4E4",
            paddingBottom: "18px",
          }}
        >
          <div
            id="rejection-reasons-title"
            style={{
              fontFamily: "Lufga",
              fontWeight: "400",
              fontStyle: "normal",
              fontSize: "24px",
              lineHeight: "100%",
              letterSpacing: "0%",
              textAlign: "center",
              color: "#191919",
            }}
          >
            Rejection Reasons
          </div>

          {/* Individual documents navigation icon */}
          {/* Individual documents navigation icon */}
          {(currentRejectedDocuments.length > 0 ||
            deletedRejectedDocuments.length > 0) && (
            <>
              {/* Current rejected documents */}
              {currentRejectedDocuments.length > 0 && (
                <button
                  type="button"
                  title="View current rejected documents"
                  onClick={showCurrentDocuments}
                  style={{
                    position: "absolute",
                    right: "72px",
                    top: "-2px",
                    border: "none",
                    background: "transparent",
                    padding: 0,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <DescriptionOutlinedIcon
                    sx={{
                      fontSize: "22px",
                      color: historyType === "current" ? "#017C57" : "#191919",
                    }}
                  />
                </button>
              )}

              {/* Deleted / past documents */}
              {deletedRejectedDocuments.length > 0 && (
                <button
                  type="button"
                  title={
                    historyType === "deleted"
                      ? "View complete document rejection"
                      : "View deleted document history"
                  }
                  onClick={showDeletedDocuments}
                  style={{
                    position: "absolute",
                    right: "42px",
                    top: "-2px",
                    border: "none",
                    background: "transparent",
                    padding: 0,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <WatchLaterOutlinedIcon
                    sx={{
                      fontSize: "22px",
                      color: historyType === "deleted" ? "#017C57" : "#191919",
                    }}
                  />
                </button>
              )}
            </>
          )}

          {/* Close */}
          <button
            type="button"
            onClick={closeModal}
            style={{
              position: "absolute",
              right: "0",
              top: "-2px",
              border: "none",
              background: "transparent",
              padding: 0,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
            }}
          >
            <CloseIcon
              sx={{
                fontSize: "22px",
                color: "#191919",
              }}
            />
          </button>
        </div>

        {/* Content */}
        {/* Content */}

        {/* Complete document rejection history */}
        {historyType === "complete" && wholeDocumentReasons.length > 0 && (
          <div
            style={{
              marginTop: "20px",
              width: "100%",
            }}
          >
            <div
              style={{
                fontFamily: "Lufga",
                fontWeight: "400",
                fontSize: "14px",
                color: "#191919",
                marginBottom: "10px",
              }}
            >
              Complete Document Rejection
            </div>

            <div
              style={{
                maxHeight: "130px",
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
              }}
            >
              {wholeDocumentReasons.map((historyItem, index) => (
                <div
                  key={index}
                  style={{
                    backgroundColor: "#F2FCF9",
                    padding: "12px",
                    minHeight: "60px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <div
                    style={{
                      fontFamily: "Lufga",
                      fontWeight: "300",
                      fontStyle: "normal",
                      fontSize: "12px",
                      lineHeight: "100%",
                      letterSpacing: "0%",
                      textAlign: "justify",
                      textTransform: "capitalize",
                      color: "#191919",
                    }}
                  >
                    {historyItem?.rejectionReason}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      marginTop: "8px",
                      fontFamily: "Lufga",
                      fontWeight: "300",
                      fontStyle: "normal",
                      fontSize: "11px",
                      lineHeight: "11px",
                      letterSpacing: "0px",
                      textAlign: "right",
                      color: "#191919",
                    }}
                  >
                    {formatRejectionDate(historyItem?.rejectionDate)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Image history section */}
        <div
          style={{
            marginTop: wholeDocumentReasons.length > 0 ? "16px" : "20px",
            width: "100%",
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {historyType === "complete" ? (
            <div
              style={{
                height: "220px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: "Lufga",
                fontSize: "14px",
                color: "#797979",
              }}
            >
              {wholeDocumentReasons.length > 0
                ? "Select the document or watch icon to view image history"
                : "No reasons found"}
            </div>
          ) : loadingDeletedDocs ? (
            <div
              style={{
                height: "220px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress
                sx={{
                  color: "#017C57",
                }}
              />
            </div>
          ) : activeRejectedDocuments.length === 0 ? (
            <div
              style={{
                height: "220px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: "Lufga",
                fontSize: "14px",
                color: "#797979",
              }}
            >
              No reasons found
            </div>
          ) : (
            <>
              {/* Current / Deleted image */}
              <div
                style={{
                  flex: "1 1 0",
                  minHeight: 0,
                  width: "100%",
                  display: "flex",
                  gap: "16px",
                  overflow: "hidden",
                }}
              >
                {/* Image */}
                <div
                  style={{
                    width: "30%",
                    border: "1px solid #E4E4E4",
                    borderRadius: "16px",
                    padding: "16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#F9F9F9",
                    overflow: "hidden",
                  }}
                >
                  {currentDocument?.documentUrl ? (
                    <img
                      src={currentDocument.documentUrl}
                      alt="Rejected document"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                        maxWidth: "100%",
                        maxHeight: "100%",
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        fontFamily: "Lufga",
                        fontSize: "14px",
                        color: "#797979",
                      }}
                    >
                      No image found
                    </div>
                  )}
                </div>

                {/* Rejection reasons */}
                <div
                  style={{
                    width: "70%",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                    overflowY: "auto",
                    paddingRight: "4px",
                  }}
                >
                  {(
                    currentDocument?.history?.rejectionReason?.filter((item) =>
                      item?.rejectionReason?.trim(),
                    ) ||
                    (currentDocument?.rejectionReason?.trim()
                      ? [
                          {
                            rejectionReason: currentDocument.rejectionReason,
                            rejectionDate: currentDocument.updatedAt,
                          },
                        ]
                      : [])
                  ).map((historyItem, index) => (
                    <div
                      key={index}
                      style={{
                        backgroundColor: "#F2FCF9",
                        padding: "12px",
                        minHeight: "70px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                      }}
                    >
                      <div
                        style={{
                          fontFamily: "Lufga",
                          fontWeight: "300",
                          fontStyle: "normal",
                          fontSize: "12px",
                          lineHeight: "100%",
                          letterSpacing: "0%",
                          textAlign: "justify",
                          textTransform: "capitalize",
                          color: "#191919",
                        }}
                      >
                        {historyItem?.rejectionReason}
                      </div>

                      <div
                        style={{
                          display: "flex",
                          justifyContent: "flex-end",
                          marginTop: "8px",
                          fontFamily: "Lufga",
                          fontWeight: "300",
                          fontStyle: "normal",
                          fontSize: "11px",
                          lineHeight: "11px",
                          letterSpacing: "0px",
                          textAlign: "right",
                          color: "#191919",
                        }}
                      >
                        {formatRejectionDate(historyItem?.rejectionDate)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Pagination */}
              {activeRejectedDocuments.length > 1 && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "16px",
                    marginTop: "10px",
                    flexShrink: 0,
                    height: "32px",
                  }}
                >
                  <button
                    type="button"
                    onClick={handlePrevious}
                    disabled={currentPage === 0}
                    style={{
                      border: "1px solid #017C57",
                      color: "#017C57",
                      backgroundColor: "white",
                      borderRadius: "20px",
                      padding: "5px 16px",
                      cursor: currentPage === 0 ? "not-allowed" : "pointer",
                      opacity: currentPage === 0 ? 0.5 : 1,
                      fontFamily: "Inter",
                      fontSize: "12px",
                    }}
                  >
                    Previous
                  </button>

                  <span
                    style={{
                      fontFamily: "Lufga",
                      fontSize: "12px",
                      color: "#191919",
                    }}
                  >
                    {currentPage + 1} / {activeRejectedDocuments.length}
                  </span>

                  <button
                    type="button"
                    onClick={handleNext}
                    disabled={
                      currentPage === activeRejectedDocuments.length - 1
                    }
                    style={{
                      border: "1px solid #017C57",
                      color: "#017C57",
                      backgroundColor: "white",
                      borderRadius: "20px",
                      padding: "5px 16px",
                      cursor:
                        currentPage === activeRejectedDocuments.length - 1
                          ? "not-allowed"
                          : "pointer",
                      opacity:
                        currentPage === activeRejectedDocuments.length - 1
                          ? 0.5
                          : 1,
                      fontFamily: "Inter",
                      fontSize: "12px",
                    }}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </Box>
    </Modal>
  );
};

export default RejectionReasonHistoryModal;
